import React from 'react';
import PropTypes from 'prop-types';

import Cookie from '@wetransfer/concorde-cookie';

const Spinner = ({
  background,
  children,
  circumference,
  className,
  displayText,
  foreground,
  onClick,
  percentage,
  rotate,
  stroke,
}) => {
  let text = null;
  percentage = Math.max(0, Math.min(100, Math.floor(percentage)));

  // Does our spinner display text in the center?
  if (displayText) {
    text = (
      <span className="spinner__label">
        {percentage}
        <span>%</span>
      </span>
    );
  }

  // Build the className
  const spinnerClassName = ['spinner'];
  if (className) {
    spinnerClassName.push(className);
  }

  // Only allow spinning if the prop is true and the cookie is not set
  const spinnerCookie = !!Cookie.get('wt_nospinner');
  if (rotate && !spinnerCookie) {
    spinnerClassName.push('spinner--rotate');
  }

  // We used these classNames to adjust positioning slightly depending
  // on the number of digits that's on screen and to determine if we need
  // to display rotation or not.
  if (percentage < 10) {
    spinnerClassName.push('spinner--single');
  } else if (percentage < 100) {
    spinnerClassName.push('spinner--double');
  } else {
    spinnerClassName.push('spinner--triple');
  }

  // Some basic math, we'll need these a few times later.
  const actualRadius = circumference / 2;
  const strokeHalf = stroke / 2;

  // The radius we'll use for math actually gets rid of the
  // stroke size too because otherwise it will be drawn partially
  // outside of view.
  const mathRadius = actualRadius - strokeHalf;
  const center = Math.PI * (mathRadius * 2);
  percentage = Math.max(0.5, percentage); // For drawing we don't want to show 0
  const strokeOffset = ((100 - percentage) / 100) * center;
  const pathLength = Math.PI * 2 * mathRadius;

  // Export our simple SVG element
  return (
    <div className={spinnerClassName.join(' ')} onClick={onClick}>
      <svg
        height={circumference}
        width={circumference}
        className="spinner__circle"
        shapeRendering="geometricPrecision"
        viewBox={`0 0 ${circumference} ${circumference}`}
      >
        <circle
          className="spinner__background"
          r={actualRadius - strokeHalf}
          cx={actualRadius}
          cy={actualRadius}
          fill="transparent"
          style={{
            strokeDasharray: pathLength,
            strokeDashoffset: 0,
            strokeWidth: stroke,
            stroke: background,
          }}
        />

        <circle
          className="spinner__foreground"
          r={actualRadius - strokeHalf}
          cx={actualRadius}
          cy={actualRadius}
          fill="transparent"
          style={{
            strokeDasharray: pathLength,
            strokeDashoffset: strokeOffset,
            strokeWidth: stroke,
            stroke: foreground,
          }}
        />
      </svg>
      {text}
      {children}
    </div>
  );
};

Spinner.propTypes = {
  background: PropTypes.string.isRequired,
  children: PropTypes.node,
  circumference: PropTypes.number.isRequired,
  className: PropTypes.string,
  displayText: PropTypes.bool.isRequired,
  foreground: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  percentage: PropTypes.number.isRequired,
  rotate: PropTypes.bool.isRequired,
  stroke: PropTypes.number.isRequired,
};

Spinner.defaultProps = {
  background: '#E8EBED',
  circumference: 170,
  displayText: true,
  foreground: '#409FFF',
  percentage: 0,
  rotate: false,
  stroke: 10,
};

export default Spinner;
